import React, { useState, useRef, useEffect } from 'react';

/**
 * "Chat with Alfred" — a lead-capture chat widget for the marketing pages.
 * Opens from a FAB or any [data-chat-open] trigger. Posts the running
 * conversation to a backend that holds the LLM key and runs Alfred's system
 * prompt (greet → ask name/business/type → qualify → answer). The key must
 * NOT live in the client. Until that route exists, the widget degrades to a
 * "call Alfred" fallback so it never dead-ends a visitor.
 */
const CHAT_ENDPOINT =
  (import.meta.env.VITE_ALFRED_CHAT_ENDPOINT) ||
  ((import.meta.env.VITE_API_BASE_URL || '') + '/alfred/chat');

const Wave = () => (
  <span className="av-wave av-wave--live" aria-hidden="true" style={{ height: 16 }}>
    <span /><span /><span /><span /><span /><span /><span />
  </span>
);

const ChatWithAlfred = ({ source = 'landing', callNumber = '(346) 258-0006', greeting: greetingOverride }) => {
  // Stays in step with the backend persona (AlfredVoice-v2 core, routes/alfredChat.js). The old
  // greeting said Alfred "answers the phones", which the repositioned site and the backend both
  // contradict — a visitor asking "so can you answer our phones?" gets told no. Outbound only.
  // Default = the CARRIER / BROKERAGE opener for the home and industry pages.
  // /drivers overrides it, because asking a CDL driver "what kind of business do
  // you run?" misidentifies them on turn one.
  //
  // Rewritten 2026-07-30: the old line offered "the patients, applicants or
  // customers nobody has time to phone" — cross-vertical language on a site that is
  // now transportation-only, and it contradicted the backend persona sitting behind
  // it. `routes/alfredChat.js` now branches on `source` (Charlie, 2026-07-30), so
  // the home page is carriers-and-brokerages end to end and this first line has to
  // match it.
  //
  // Carriers and brokerages are named SEPARATELY on purpose. A brokerage otherwise
  // has to work out that it counts as "a business", and the backend keeps a real
  // split between them — never pitch driver recruiting at a brokerage, they'll know.
  const greeting = greetingOverride
    || "Hi — I'm Alfred, an AI. I make the calls a carrier or brokerage doesn't have time for — driver applicants, check calls, chasing the list that's already sitting in your system. Are you running trucks, or booking freight?";
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {role, content}
  const logRef = useRef(null);

  useEffect(() => {
    const openChat = () => setOpen(true);
    const triggers = document.querySelectorAll('[data-chat-open]');
    triggers.forEach((t) => t.addEventListener('click', openChat));
    return () => triggers.forEach((t) => t.removeEventListener('click', openChat));
  }, []);

  useEffect(() => {
    if (open && !started) {
      setMessages([{ role: 'assistant', content: greeting }]);
      setStarted(true);
    }
  }, [open, started]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next, source })
      });
      if (!res.ok) throw new Error('bad_status');
      const data = await res.json();
      const reply = data?.reply || data?.message ||
        `Thanks! The fastest way to hear Alfred is to call ${callNumber}.`;
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: `I had trouble connecting just now. The fastest way to hear Alfred is to call ${callNumber} — or leave your name and number and we'll ring you.`
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Chat with Alfred"
          className="fixed right-5 bottom-5 z-50 inline-flex items-center gap-2 bg-ink text-white px-5 py-3 rounded-full shadow-xl hover:bg-inksoft text-xs font-semibold uppercase tracking-[0.1em]"
        >
          <Wave /> Chat with Alfred
        </button>
      )}

      {open && (
        <aside
          role="dialog"
          aria-label="Chat with Alfred"
          className="fixed right-5 bottom-5 z-50 flex flex-col bg-paper border border-hair rounded-brand shadow-2xl overflow-hidden"
          style={{ width: 'min(380px, calc(100vw - 32px))', height: 'min(560px, calc(100vh - 40px))' }}
        >
          <div className="flex items-center gap-2.5 bg-ink text-white px-4 py-3">
            <Wave />
            <div className="leading-tight">
              <div className="font-semibold text-sm">Alfred</div>
              <div className="text-mist text-[0.7rem]">AI Voice · usually replies instantly</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="ml-auto text-mist text-2xl leading-none hover:text-white">×</button>
          </div>

          <div ref={logRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5 bg-warm">
            {messages.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === 'user'
                    ? 'self-end max-w-[84%] bg-ink text-white rounded-2xl rounded-br-sm px-3.5 py-2 text-sm'
                    : 'self-start max-w-[84%] bg-paper border border-hair rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-ink'
                }
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="self-start bg-paper border border-hair rounded-2xl rounded-bl-sm px-3.5 py-2"><Wave /></div>
            )}
          </div>

          <form onSubmit={send} className="flex gap-2 p-3 border-t border-hair bg-paper">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
              autoComplete="off"
              placeholder="Tell Alfred about your business…"
              aria-label="Message Alfred"
              className="flex-1 border border-hair rounded-full px-4 py-2 text-sm text-ink focus:outline-none focus:border-brass focus:ring-2 focus:ring-brass/20"
            />
            <button type="submit" disabled={busy} aria-label="Send" className="flex-shrink-0 w-10 h-10 rounded-full bg-brass text-white hover:bg-brassdeep disabled:opacity-50">➤</button>
          </form>
        </aside>
      )}
    </>
  );
};

export default ChatWithAlfred;
